import { CharacteristicInfo } from "./GATT";
import { useState, useRef, useMemo } from "preact/hooks";
import { h } from "preact";
import { ValueDisplay } from "./ValueDisplay";
import { encode, decodeStream } from "@msgpack/msgpack";
import { encode as encodeLite } from "msgpack-lite";
import { DiscoveredCharacteristic } from "./DiscoveredService";
import { NoticeBlock } from "./NoticeBlock";
import Editor from "react-simple-code-editor";
import jsonFormatHighlight from "json-format-highlight";

export function Characteristic({ characteristics, characteristic: c }: {
    characteristics: CharacteristicInfo[],
    characteristic: DiscoveredCharacteristic
}) {
    const [hidden, setHidden] = useState(false);

    const relatedCharacteristc = c.characteristic.uuid.endsWith("-0000-1000-8000-00805f9b34fb")
    ? characteristics.find(ch => ch.uuid.toLowerCase() === "0x" + c.characteristic.uuid.substr(4, 4))
    : characteristics.find(ch => ch.uuid.toLowerCase() === c.characteristic.uuid);

    const name = relatedCharacteristc ? relatedCharacteristc.name : "";
    const propertyKeys = [
        "authenticatedSignedWrites",
        "broadcast",
        "indicate",
        "notify",
        "read",
        "reliableWrite",
        "writableAuxiliaries",
        "write",
        "writeWithoutResponse"
    ];

    const context: { update?: () => void } = {};

    return <div class="characteristic">
        <div class="characteristic-header" style={{ cursor: "pointer" }} onClick={() => setHidden(!hidden)}>
            <div>Characteristic UUID: {c.characteristic.uuid}</div>
            {name ? <div>Characteristic Name: {name}</div> : null}
        </div>
        <div class={"content" + (hidden ? " hidden" : "")}>
            <div>Properties:</div>
            <div class="properties" style={{ marginLeft: "20px", marginBottom: "10px" }}>
                {propertyKeys.map(p => <div class="header">{p}</div>)}
                {propertyKeys.map(p => <div class={"value" + ((c.characteristic.properties as any)[p] ? " badge success material-icons" : "")}>
                    {(c.characteristic.properties as any)[p] ? "check": ""}
                </div>)}
            </div>
            {c.characteristic.properties.read ? <div>
                <ReadValue characteristic={c.characteristic} context={context}/>
            </div> : null}
            {c.characteristic.properties.write ? <div>
                <WriteValue characteristic={c.characteristic}/>
                <MsgPackBenchmark characteristic={c.characteristic}/>
            </div> : null}
            {c.characteristic.properties.notify || c.characteristic.properties.indicate ? <div>
                <NoticeBlock type="warning" style={{ fontWeight: "bold" }}>Warning (BUG): If you start both notifies then both will stop when one of them is stopped</NoticeBlock>
                <Notification characteristic={c.characteristic} context={context}/>
            </div> : null}
            {c.characteristic.properties.notify || c.characteristic.properties.indicate ? <div>
                <NotificationMsgPack characteristic={c.characteristic}/>
            </div> : null}
            <div>Descriptors:</div>
            <div>
                <div style={{ marginLeft: "20px" }}>
                    {c.descriptors.map(d => <div>
                        {d.uuid}
                        <ReadValue characteristic={d}/>
                        <WriteValue characteristic={d}/>
                    </div>)}
                </div>
            </div>
        </div>
    </div>;
}


function ReadValue(props: {
    characteristic: BluetoothRemoteGATTCharacteristic | BluetoothRemoteGATTDescriptor,
    context?: { update?: (refresh?: boolean) => void }
}) {
    const [value, setValue] = useState<DataView | null>(null);
    const [status, setStatus] = useState<string | { error: string }>("");
    const read = async (refresh?: boolean) => {
        try {
            if (refresh && props.characteristic.value) {
                setValue(props.characteristic.value);
            }
            else {
                setValue(await props.characteristic.readValue());
            }

            setStatus(`Read complete`);
        }
        catch(ex) {
            setStatus({ error: `Error: ${ex}` });
        }
    };

    if (props.context) {
        props.context.update = read;
    }

    return <div class="read-value paper">
        <button onClick={() => read()}>Read Value</button>
        <ValueDisplay data={value}/>
        {typeof status === "string" ? (status ? <span class="badge success">{status}</span> : "") : <div class="badge error">{status.error}</div>}
    </div>;
}

function WriteValue(props: { characteristic: BluetoothRemoteGATTCharacteristic | BluetoothRemoteGATTDescriptor }) {
    const valueRef = useRef<HTMLInputElement>();
    const [status, setStatus] = useState<string | { error: string }>("");
    const [valueType, setValueType] = useState("hex");
    const [multiline, setMultiline] = useState(false);
    const [value, setValue] = useState("");

    const placeholderTexts: { [key: string]: string } = {
        hex: "hex byte value",
        utf8: "utf8 string",
        msgpack: "json string",
        "msgpack-lite": "json string",
    };

    const customColorOptions = {
        keyColor: "#0451a5",
        numberColor: "#098658",
        stringColor: "#a31515",
        trueColor: "#0000ff",
        falseColor: "#0000ff",
        nullColor: "#0000ff"
    };

    const customDarkColorOptions = {
        keyColor: "#9cdcfe",
        numberColor: "#b5cea8",
        stringColor: "#ce9178",
        trueColor: "#569cd6",
        falseColor: "#569cd6",
        nullColor: "#569cd6"
    };

    return <div class="write-value paper">
        <div class="type">
            <select onChange={(e) => setValueType(e.currentTarget.value)}>
                <option value="hex">Hex</option>
                <option value="utf8">UTF8</option>
                <option value="msgpack">MsgPack</option>
                <option value="msgpack-lite">MsgPack Lite</option>
            </select> Multiline: <input type="checkbox" onChange={(e) => setMultiline(e.currentTarget.checked)}/>
        </div>
        <div class="value">
            {multiline
                ? <Editor
                    style={{ background: "white", color: "black", minHeight: "100px", border: "1px solid #afafaf", fontFamily: "monospace", fontSize: 13 }}
                    value={value}
                    padding={5}
                    onValueChange={(code: string) => setValue(code)}
                    placeholder={placeholderTexts[valueType]}
                    highlight={(valueType === "msgpack" || valueType === "msgpack-lite")
                        ? (code: string) => jsonFormatHighlight(code, customColorOptions)
                        : (code: string) => code}/>
                : <input value={value} onChange={(e) => setValue(e.currentTarget.value)} placeholder={placeholderTexts[valueType]}/>}
        </div>
        <div>
            <button onClick={async () => {
                let buffer: Uint8Array;
                let preview;

                if (valueType === "hex") {
                    const values = parseHexString(value.replace(/0x/mg, "").replace(/\s/mg, ""));
                    buffer = new Uint8Array(values.length);
                    buffer.set(values);

                    preview = values.map(v => "0x" + v.toString(16)).join(", ");
                    setStatus(`Writing... (${preview})`);
                }
                else if (valueType === "msgpack") {
                    buffer = encode(JSON.parse(value));
                    preview = Array.from(buffer).map(v => "0x" + v.toString(16)).join(", ");

                    setStatus(`Writing... (${preview})`);

                }
                else if (valueType === "msgpack-lite") {
                    buffer = encodeLite(JSON.parse(value));
                    preview = Array.from(buffer).map(v => "0x" + v.toString(16)).join(", ");

                    setStatus(`Writing... (${preview})`);
                }
                else {
                    const encoder = new TextEncoder();
                    buffer = encoder.encode(value);
                    preview = Array.from(buffer).map(v => "0x" + v.toString(16)).join(", ");

                    setStatus(`Writing... (${preview})`);
                }

                try {
                    await props.characteristic.writeValue(buffer);
                } catch (error) {
                    setStatus({ error: `Error: ${error}` });
                    return;
                }

                setStatus(`Written (${preview})`);
            }}>Write Value</button>
        </div>
        {typeof status === "string" ? status : <div style={{ color: "red" }}>{status.error}</div>}
    </div>;
}


function Notification(props: { characteristic: BluetoothRemoteGATTCharacteristic, context: { update?: (refresh?: boolean) => void } }) {
    const [notificationHandler, setNotificationHandler] = useState<{ callback: (e: Event) => void } | null>(null);
    const [indicateValue, setIndicateValue] = useState<DataView | null>(null);
    const [notifyCount, setNotifyCount] = useState(0);
    const [timeSpan, setTimeSpan] = useState(0);
    const [timeSpanAvg, setTimeSpanAvg] = useState(0);
    const perfRef = useRef<{ count: number, lastTime: undefined | number, timeSpanAvg: number }>();
    perfRef.current = useMemo(() => ({ count: 0, lastTime: undefined, timeSpanAvg: 0 }), []);

    return <div class="notification paper">
        <button class={notificationHandler ? "error" : ""} onClick={async () => {
            if (!notificationHandler) {
                const handler = {
                    callback: (e: Event) => {
                        if ((e.target as any).value) {
                            const view = (e.target as any).value as DataView;
                            setIndicateValue(view);
                        }

                        if (props.context.update) {
                            props.context.update(true);
                        }

                        perfRef.current!.count++;

                        if (perfRef.current!.lastTime) {
                            const delta = performance.now() - perfRef.current!.lastTime;
                            setTimeSpan(delta);
                            perfRef.current!.timeSpanAvg += delta;
                            setTimeSpanAvg(perfRef.current!.timeSpanAvg / perfRef.current!.count);
                        }

                        perfRef.current!.lastTime = performance.now();

                        setNotifyCount(perfRef.current!.count);
                    }
                };

                setNotificationHandler(handler);
                props.characteristic.addEventListener("characteristicvaluechanged", handler.callback);

                await props.characteristic.startNotifications();
            }
            else {
                await props.characteristic.stopNotifications();
                props.characteristic.removeEventListener("characteristicvaluechanged", notificationHandler.callback);
                setNotificationHandler(null);
            }
        }}>{notificationHandler ? "Stop notify" : "Start notfiy"}</button>
        <ValueDisplay data={indicateValue}/>
        <div>
            Notification count: <span style={{
                display: "inline-block",
                minWidth: "50px",
                fontFamily: "monospace",
                textAlign: "right",
                padding: "2px",
                backgroundColor: "#3f3f3f"
            }}>{notifyCount}</span>,
            Notification time span: <span style={{
                display: "inline-block",
                minWidth: "50px",
                fontFamily: "monospace",
                textAlign: "right",
                padding: "2px",
                backgroundColor: "#3f3f3f"
            }}>{timeSpan.toFixed(2)}</span> ms,
            Notification time span (average): <span style={{
                display: "inline-block",
                minWidth: "50px",
                fontFamily: "monospace",
                textAlign: "right",
                padding: "2px",
                backgroundColor: "#3f3f3f"
            }}>{timeSpanAvg.toFixed(2)}</span> ms
            <button style={{ marginLeft: "10px" }} onClick={() => {
                perfRef.current!.count = 0;
                perfRef.current!.lastTime = undefined;
                perfRef.current!.timeSpanAvg = 0;
                setTimeSpan(0);
                setTimeSpanAvg(0);
                setNotifyCount(0);
            }}>Reset</button>
        </div>
    </div>;
}
function MsgPackBenchmark(props: { characteristic: BluetoothRemoteGATTCharacteristic }) {
    const [temp, setTemp] = useState(false);
    const [notifyCount, setPacketCount] = useState(0);
    const [timeSpan, setTimeSpan] = useState(0);
    const [timeSpanAvg, setTimeSpanAvg] = useState(0);
    const perfRef = useRef<{  isRunning: boolean, count: number, lastTime: undefined | number, timeSpanAvg: number }>();
    perfRef.current = useMemo(() => ({ isRunning: false, count: 0, lastTime: undefined, timeSpanAvg: 0 }), []);

    return <div class="notification paper">
        <div style={{ fontSize: "20px", marginBottom: "10px"}}>Message pack benchmark</div>
        <button onClick={async () => {
            if (perfRef.current!.isRunning) {
                perfRef.current!.isRunning = false;
                setTemp(false);
                return;
            }

            perfRef.current!.isRunning = true;
            setTemp(true);

            const json = {
                ping: {
                    number: 0,
                    data: "01234567890123456789012345678901234567890123456789" +
                          "01234567890123456789012345678901234567890123456789" +
                          "01234567890123456789012345678901234567890123456789" +
                          "01234567890123456789012345678901234567890123456789" +
                          "01234567890123456789012345678901234567890123456789" +
                          "01234567890123456789012345678901234567890123456789" +
                          "01234567890123456789012345678901234567890123456789" +
                          "01234567890123456789012345678901234567890123456789" +
                          "01234567890123456789012345678901234567890123456789",
                        //   "0123456789012345678901234567890",
                    resp: false
                }
            }

            const timeStart = performance.now();
            let avg = 0;
            while(perfRef.current!.isRunning) {
                const ts = performance.now();
                const buffer = encode(json);

                await props.characteristic.writeValue(buffer)

                json.ping.number++;
                setPacketCount(json.ping.number);

                if (json.ping.number === 50) {
                    setTemp(false);
                    perfRef.current!.isRunning = false;
                    break;
                }
                avg += performance.now() - ts;
                setTimeSpanAvg(avg / json.ping.number);
            }

            setTimeSpan(performance.now() - timeStart);
        }}>{perfRef.current!.isRunning ? "Stop benchmark" : "Start benchmark"}</button>
        {/* <ValueDisplay data={indicateValue}/> */}
        <div>
            Packet index: <span style={{
                display: "inline-block",
                minWidth: "50px",
                fontFamily: "monospace",
                textAlign: "right",
                padding: "2px",
                backgroundColor: "#3f3f3f"
            }}>{notifyCount}</span>,
            Benchmark full time: <span style={{
                display: "inline-block",
                minWidth: "50px",
                fontFamily: "monospace",
                textAlign: "right",
                padding: "2px",
                backgroundColor: "#3f3f3f"
            }}>{timeSpan.toFixed(2)}</span> ms,
            Benchmark packet time span (average): <span style={{
                display: "inline-block",
                minWidth: "50px",
                fontFamily: "monospace",
                textAlign: "right",
                padding: "2px",
                backgroundColor: "#3f3f3f"
            }}>{timeSpanAvg.toFixed(2)}</span> ms
            <button style={{ marginLeft: "10px" }} onClick={() => {
                perfRef.current!.count = 0;
                perfRef.current!.lastTime = undefined;
                perfRef.current!.timeSpanAvg = 0;
                setTimeSpan(0);
                setTimeSpanAvg(0);
                setPacketCount(0);
            }}>Reset</button>
        </div>
    </div>;
}


function createAsyncDataBuffer() {
    const obj = {
        buffer: [] as ArrayLike<number>[],
        lock: null as Promise<ArrayLike<number>[] | null> | null,
        // transaction: 0,
        resetIndex: 0,
        lastTime: undefined as number | undefined,
        timeSpanAverage: 0,
        count: 0,
        lockResolve: null as ((data: ArrayLike<number>[] | null) => void) | null,
        add(data: ArrayLike<number>) {
            if (this.lockResolve) {
                this.lockResolve(this.buffer);
                this.lockResolve = null;
                this.lock = new Promise<ArrayLike<number>[] | null>((resolve) => this.lockResolve = resolve);
            }

            this.buffer.push(data);
        },
        end() {
            if (this.lockResolve) {
                this.lockResolve(null);
                this.lockResolve = null;
                this.lock = null;
            }
        },
        async* [Symbol.asyncIterator]() {
            while(this.lock) {
                const data = await this.lock;

                if (data) {
                    for (let i = 0; i < data.length; i++) {
                        yield data[i];
                    }

                    data.splice(0, data.length);
                }
                else {
                    break;
                }
            }
        }
    };

    obj.lock = new Promise<ArrayLike<number>[] | null>((resolve) => obj.lockResolve = resolve);

    return obj;
}

function NotificationMsgPack(props: { characteristic: BluetoothRemoteGATTCharacteristic }) {
    const [notificationHandler, setNotificationHandler] = useState<{ callback: (e: Event) => void } | null>(null);
    const [indicateValue, setIndicateValue] = useState<any | null>(null);
    const [notifyCount, setNotifyCount] = useState(0);
    const [timeSpan, setTimeSpan] = useState(0);
    const [timeSpanAvg, setTimeSpanAvg] = useState(0);

    const [msgpackStreamingReset, setMsgpackStreamingReset] = useState(0);
    const msgpackStreaming = useRef<ReturnType<typeof createAsyncDataBuffer>>();
    msgpackStreaming.current = useMemo(createAsyncDataBuffer, [msgpackStreamingReset]);
    const decodingHandler = useMemo(() => {
        return {
            isRunning: false,
            start() {
                this.isRunning = true;
                this.run();
            },
            async run() {
                try {
                    for await (const obj of decodeStream(msgpackStreaming.current!)) {
                        console.log(obj);
                        setIndicateValue(obj);
                        // msgpackStreaming.current!.transaction = 0;
                    }
                }
                catch(ex) {
                    console.error(ex);
                    msgpackStreaming.current!.resetIndex++;
                    setMsgpackStreamingReset(msgpackStreaming.current!.resetIndex);
                }

                if (this.isRunning) {
                    this.run();
                }
            },
            stop() {
                this.isRunning = false;
            }
        };
    }, []);

    return <div class="notification paper">
        <div style={{ fontSize: "20px", marginBottom: "10px"}}>Message pack stream read</div>
        <button class={notificationHandler ? "error": ""} onClick={async () => {
            if (!notificationHandler) {
                const handler = {
                    callback: (e: Event) => {
                        if ((e.target as any).value) {
                            const view = (e.target as any).value as DataView;

                            // if (msgpackStreaming.current!.transaction === view.getUint8(0)) {
                                // msgpackStreaming.current!.add(new Uint8Array(view.buffer, 1));
                                // msgpackStreaming.current!.transaction++;
                            // }
                            // else {
                            //     msgpackStreaming.current!.transaction = 0;
                            //     msgpackStreaming.current!.resetIndex++;
                            //     setMsgpackStreamingReset(msgpackStreaming.current!.resetIndex);
                            // }

                            msgpackStreaming.current!.add(new Uint8Array(view.buffer));
                        }

                        msgpackStreaming.current!.count++;

                        if (msgpackStreaming.current!.lastTime) {
                            const delta = performance.now() - msgpackStreaming.current!.lastTime;
                            setTimeSpan(delta);
                            msgpackStreaming.current!.timeSpanAverage += delta;
                            setTimeSpanAvg(msgpackStreaming.current!.timeSpanAverage / msgpackStreaming.current!.count);
                        }

                        msgpackStreaming.current!.lastTime = performance.now();

                        setNotifyCount(msgpackStreaming.current!.count);
                    }
                };

                setNotificationHandler(handler);
                props.characteristic.addEventListener("characteristicvaluechanged", handler.callback);

                await props.characteristic.startNotifications();
                decodingHandler.start();
            }
            else {
                await props.characteristic.stopNotifications();
                props.characteristic.removeEventListener("characteristicvaluechanged", notificationHandler.callback);
                setNotificationHandler(null);
                decodingHandler.stop();
            }
        }}>{notificationHandler ? "Stop notify" : "Start notfiy"}</button>
        <div class="value" style={{ }}>
            <code style={{ whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
                {indicateValue ? JSON.stringify(indicateValue, undefined, 4) : null}
            </code>
        </div>
        <div>
            Notification count: <span style={{
                display: "inline-block",
                minWidth: "50px",
                fontFamily: "monospace",
                textAlign: "right",
                padding: "2px",
                backgroundColor: "#3f3f3f"
            }}>{notifyCount}</span>,
            Notification time span: <span style={{
                display: "inline-block",
                minWidth: "50px",
                fontFamily: "monospace",
                textAlign: "right",
                padding: "2px",
                backgroundColor: "#3f3f3f"
            }}>{timeSpan.toFixed(2)}</span> ms,
            Notification time span (average): <span style={{
                display: "inline-block",
                minWidth: "50px",
                fontFamily: "monospace",
                textAlign: "right",
                padding: "2px",
                backgroundColor: "#3f3f3f"
            }}>{timeSpanAvg.toFixed(2)}</span> ms
            <button style={{ marginLeft: "10px" }} onClick={() => {
                msgpackStreaming.current!.count = 0;
                msgpackStreaming.current!.lastTime = undefined;
                msgpackStreaming.current!.timeSpanAverage = 0;
                setTimeSpan(0);
                setTimeSpanAvg(0);
                setNotifyCount(0);
            }}>Reset</button>
        </div>
    </div>;
}

function parseHexString(str: string) {
    const result = [];
    while (str.length >= 2) {
        result.push(parseInt(str.substring(0, 2), 16));

        str = str.substring(2, str.length);
    }

    return result;
}
